import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface SessionExpiryDialogProps {
  /** Whether the inactivity warning is currently visible. */
  open: boolean;
  /** Whole seconds remaining until automatic logout. */
  secondsLeft: number;
  /** True while a "Stay logged in" token refresh is in flight. */
  isRefreshing: boolean;
  /** Extend the session (reset idle timer + refresh token). */
  onStay: () => void;
  /** Log out immediately. */
  onLogout: () => void;
}

/**
 * Presentational warning shown when a user has been inactive for nearly the
 * idle limit. All state (countdown, refresh, logout) is owned by useIdleTimeout;
 * this component just renders it. The countdown is supplied as a timestamp-derived
 * value, so it stays accurate even after the tab was backgrounded.
 */
export const SessionExpiryDialog = ({
  open,
  secondsLeft,
  isRefreshing,
  onStay,
  onLogout,
}: SessionExpiryDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        // Prevent closing by clicking outside or pressing Escape
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Your session is about to expire</DialogTitle>
          <DialogDescription>
            You've been inactive for a while. Would you like to stay logged in?
            You will be automatically logged out in{" "}
            <span className="font-semibold text-foreground">{secondsLeft}</span>{" "}
            second{secondsLeft !== 1 ? "s" : ""}.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-3">
          <Button variant="outline" onClick={onLogout} disabled={isRefreshing}>
            Log out
          </Button>
          <Button onClick={onStay} disabled={isRefreshing}>
            {isRefreshing ? "Refreshing…" : "Stay logged in"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
